import { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
  type AttachmentKind,
  type SessionAttachment,
  confirmAttachmentForMySession,
  confirmAttachmentForTenant,
  createAttachmentForMySession,
  createAttachmentForTenantSession,
  deletePendingAttachmentForMySession,
  deletePendingAttachmentForTenant,
  listAttachmentsForMySession,
  listAttachmentsForTenantSession,
  putToPresignedUrl,
} from '../api/sessionAttachments';
import { extractErrorMessage } from '../api/errors';

/** 5 GB — matches the server-side cap in
 *  `app/schemas/session_attachment.MAX_ATTACHMENT_BYTES`. */
const MAX_BYTES = 5 * 1024 * 1024 * 1024;

interface AttachmentsDialogProps {
  open: boolean;
  onClose: () => void;
  /** CaptureSession PK UUID (matches the existing `/download`
   *  endpoint shape — NOT the capture-app's str(64) session id). */
  sessionRowId: string;
  /** Display label (participant name, etc) so the dialog header
   *  reminds the operator which session they're attaching to. */
  sessionLabel: string;
  /** When provided → tenant-admin path. When omitted → participant
   *  ("me") path that uses the current user's auth + cross-tenant
   *  resolution server-side. */
  tenantId?: string;
}

/** A file staged for upload — its auto-detected (user-overridable) kind
 *  plus a stable id so the list survives kind edits + removals. */
interface StagedFile {
  id: string;
  file: File;
  kind: AttachmentKind;
}

/**
 * What the operator sees for a file's type. Procedure media is "Session …";
 * a debrief recorded afterwards is "Coaching …", named after what the file
 * actually is (one API kind, `coaching`, for both).
 */
export function kindLabel(kind: string, isVideo: boolean): string {
  if (kind === 'coaching') return isVideo ? 'Coaching video' : 'Coaching audio';
  if (kind === 'video') return 'Session video';
  return 'Session audio';
}

function isVideoFile(file: File): boolean {
  return detectKindFromFile(file) === 'video';
}

function detectKindFromFile(file: File): AttachmentKind {
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  // Fallback by extension when the OS didn't supply a MIME type.
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (['mp3', 'm4a', 'wav', 'ogg', 'flac', 'aac', 'opus'].includes(ext)) {
    return 'audio';
  }
  return 'video';
}

/** Identity for de-duplication: same filename + byte size. We can't hash
 *  multi-GB media cheaply in the browser, so name+size is the practical
 *  "is this the same file?" key — matching what the server stores. */
function fileKey(name: string, size: number): string {
  return `${name}\u0000${size}`;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function AttachmentsDialog({
  open,
  onClose,
  sessionRowId,
  sessionLabel,
  tenantId,
}: AttachmentsDialogProps) {
  const isAdmin = tenantId !== undefined;

  const [items, setItems] = useState<SessionAttachment[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  // Files staged for upload — pick several (e.g. a video + its audio) in
  // one go; each uploads via the existing per-file create→PUT→confirm.
  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(
    null,
  );
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Notice when a picked file was skipped because it's already attached
  // to the session or already in the staged list.
  const [dupeNotice, setDupeNotice] = useState<string | null>(null);
  /** id of the row whose delete is in flight, so we can disable the
   *  X icon and the rest of the row during the call. */
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Monotonic id for staged files (stable React keys without pulling in
  // crypto.randomUUID, which isn't in every test/browser environment).
  const stagedIdRef = useRef(0);

  const reload = useCallback(async () => {
    setListError(null);
    try {
      const rows = isAdmin
        ? await listAttachmentsForTenantSession(tenantId, sessionRowId)
        : await listAttachmentsForMySession(sessionRowId);
      setItems(rows);
    } catch (err: unknown) {
      setListError(extractErrorMessage(err, 'failed to load attachments'));
      setItems([]);
    }
  }, [isAdmin, sessionRowId, tenantId]);

  useEffect(() => {
    if (!open) return;
    setItems(null);
    setStaged([]);
    setProgress(null);
    setUploadError(null);
    setDupeNotice(null);
    void reload();
  }, [open, reload]);

  const handlePickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    // Clear the input so re-picking the same file fires onChange again.
    e.target.value = '';
    if (picked.length === 0) return;

    // Skip files already attached to the session or already staged —
    // same name + size. Dedupe within this batch too.
    const seen = new Set<string>([
      ...(items ?? []).map((r) => fileKey(r.original_filename, r.size_bytes)),
      ...staged.map((s) => fileKey(s.file.name, s.file.size)),
    ]);
    const additions: StagedFile[] = [];
    const skipped: string[] = [];
    for (const file of picked) {
      const key = fileKey(file.name, file.size);
      if (seen.has(key)) {
        skipped.push(file.name);
        continue;
      }
      seen.add(key);
      additions.push({
        id: `f${stagedIdRef.current++}`,
        file,
        kind: detectKindFromFile(file),
      });
    }

    setUploadError(null);
    setDupeNotice(
      skipped.length > 0
        ? `Already added or attached — skipped ${[...new Set(skipped)].join(', ')}.`
        : null,
    );
    if (additions.length > 0) {
      setStaged((prev) => [...prev, ...additions]);
    }
  };

  const setStagedKind = (id: string, next: AttachmentKind) =>
    setStaged((prev) =>
      prev.map((s) => (s.id === id ? { ...s, kind: next } : s)),
    );

  const removeStaged = (id: string) => {
    setStaged((prev) => prev.filter((s) => s.id !== id));
    setDupeNotice(null);
  };

  const handleDeletePending = async (attachmentId: string) => {
    setDeletingId(attachmentId);
    setListError(null);
    try {
      if (isAdmin) {
        await deletePendingAttachmentForTenant(tenantId, attachmentId);
      } else {
        await deletePendingAttachmentForMySession(sessionRowId, attachmentId);
      }
      await reload();
    } catch (err: unknown) {
      setListError(extractErrorMessage(err, 'delete failed'));
    } finally {
      setDeletingId(null);
    }
  };

  const uploadOne = async (s: StagedFile) => {
    const payload = {
      kind: s.kind,
      original_filename: s.file.name,
      content_type: s.file.type || 'application/octet-stream',
      size_bytes: s.file.size,
    };
    const created = isAdmin
      ? await createAttachmentForTenantSession(tenantId, sessionRowId, payload)
      : await createAttachmentForMySession(sessionRowId, payload);
    await putToPresignedUrl(created.presigned_put_url, s.file);
    if (isAdmin) {
      await confirmAttachmentForTenant(tenantId, created.attachment.id);
    } else {
      await confirmAttachmentForMySession(sessionRowId, created.attachment.id);
    }
  };

  const handleUpload = async () => {
    if (staged.length === 0) return;

    // Backstop the pick-time dedupe: if the file list was still loading
    // when files were picked, drop any that turn out to match an existing
    // attachment before we hit the network.
    const existingKeys = new Set(
      (items ?? []).map((r) => fileKey(r.original_filename, r.size_bytes)),
    );
    const dupes = staged.filter((s) =>
      existingKeys.has(fileKey(s.file.name, s.file.size)),
    );
    const toUpload = staged.filter(
      (s) => !existingKeys.has(fileKey(s.file.name, s.file.size)),
    );
    if (dupes.length > 0) {
      setDupeNotice(
        `Already attached — skipped ${[
          ...new Set(dupes.map((d) => d.file.name)),
        ].join(', ')}.`,
      );
      setStaged(toUpload);
    }
    if (toUpload.length === 0) return;

    const tooBig = toUpload.find((s) => s.file.size > MAX_BYTES);
    if (tooBig) {
      setUploadError(
        `"${tooBig.file.name}" is larger than the 5 GB cap ` +
          `(${formatBytes(tooBig.file.size)})`,
      );
      return;
    }
    setUploading(true);
    setUploadError(null);
    setProgress({ done: 0, total: toUpload.length });
    // Upload sequentially; a failure on one file is recorded but doesn't
    // abort the rest. Successful files drop out of the list, failed ones
    // stay staged so the user can retry just those.
    const succeeded = new Set<string>();
    const failures: string[] = [];
    for (let i = 0; i < toUpload.length; i++) {
      const s = toUpload[i];
      try {
        await uploadOne(s);
        succeeded.add(s.id);
      } catch (err: unknown) {
        failures.push(`${s.file.name}: ${extractErrorMessage(err, 'upload failed')}`);
      } finally {
        setProgress({ done: i + 1, total: toUpload.length });
      }
    }
    setStaged((prev) => prev.filter((s) => !succeeded.has(s.id)));
    setUploadError(failures.length > 0 ? failures.join('\n') : null);
    setProgress(null);
    setUploading(false);
    await reload();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" component="span">
            Media for session
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block' }}
          >
            {sessionLabel}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close" disabled={uploading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Existing files
            </Typography>
            {items === null ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : listError ? (
              <Alert severity="error">{listError}</Alert>
            ) : items.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No files uploaded for this session yet.
              </Typography>
            ) : (
              <List dense disablePadding>
                {items.map((row) => (
                  <ListItem
                    key={row.id}
                    disableGutters
                    secondaryAction={
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                      >
                        <Chip
                          size="small"
                          label={
                            row.uploaded_at
                              ? kindLabel(row.kind, row.content_type.startsWith('video/'))
                              : 'pending'
                          }
                          color={
                            row.uploaded_at
                              ? row.kind === 'video'
                                ? 'primary'
                                : row.kind === 'coaching'
                                  ? 'success'
                                  : 'secondary'
                              : 'default'
                          }
                        />
                        {row.uploaded_at === null && (
                          <IconButton
                            size="small"
                            aria-label={`Clear pending upload ${row.original_filename}`}
                            disabled={deletingId === row.id}
                            onClick={() => void handleDeletePending(row.id)}
                          >
                            {deletingId === row.id ? (
                              <CircularProgress size={14} />
                            ) : (
                              <DeleteOutlineIcon fontSize="small" />
                            )}
                          </IconButton>
                        )}
                      </Stack>
                    }
                  >
                    <ListItemText
                      primary={row.original_filename}
                      secondary={`${formatBytes(row.size_bytes)} · uploaded ${
                        row.uploaded_at
                          ? new Date(row.uploaded_at).toLocaleString()
                          : '(pending)'
                      }`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Upload files
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Session video / audio</strong> is the procedure itself —
              lined up with the recording by its sound.{' '}
              <strong>Coaching video / audio</strong> is a debrief recorded
              afterwards: it keeps its own timeline and is transcribed in full.
            </Typography>
            <Stack spacing={1.5}>
              <Button variant="outlined" component="label" disabled={uploading}>
                {staged.length > 0
                  ? 'Add more files'
                  : 'Choose video and/or audio files'}
                <input
                  type="file"
                  accept="video/*,audio/*"
                  multiple
                  hidden
                  onChange={handlePickFiles}
                />
              </Button>
              {dupeNotice && (
                <Alert severity="info" onClose={() => setDupeNotice(null)}>
                  {dupeNotice}
                </Alert>
              )}
              {staged.length > 0 && (
                <List dense disablePadding>
                  {staged.map((s) => (
                    <ListItem
                      key={s.id}
                      disableGutters
                      secondaryAction={
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                        >
                          <Select
                            size="small"
                            value={s.kind}
                            onChange={(e) =>
                              setStagedKind(
                                s.id,
                                e.target.value as AttachmentKind,
                              )
                            }
                            disabled={uploading}
                            sx={{ minWidth: 150 }}
                          >
                            <MenuItem value="video">Session video</MenuItem>
                            <MenuItem value="audio">Session audio</MenuItem>
                            {/* A debrief recorded after the procedure — on
                                its own timeline, transcribed in full. */}
                            <MenuItem value="coaching">
                              {kindLabel('coaching', isVideoFile(s.file))}
                            </MenuItem>
                          </Select>
                          <IconButton
                            size="small"
                            aria-label={`Remove ${s.file.name}`}
                            disabled={uploading}
                            onClick={() => removeStaged(s.id)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      }
                    >
                      <ListItemText
                        primary={s.file.name}
                        secondary={`${formatBytes(s.file.size)} · ${
                          s.file.type || 'unknown type'
                        }`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              {uploading && (
                <Box>
                  <LinearProgress
                    variant={progress ? 'determinate' : 'indeterminate'}
                    value={
                      progress
                        ? (progress.done / progress.total) * 100
                        : undefined
                    }
                  />
                  {progress && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5, display: 'block' }}
                    >
                      Uploading {Math.min(progress.done + 1, progress.total)} of{' '}
                      {progress.total}…
                    </Typography>
                  )}
                </Box>
              )}
              {uploadError && (
                <Alert
                  severity="error"
                  onClose={() => setUploadError(null)}
                  sx={{ whiteSpace: 'pre-line' }}
                >
                  {uploadError}
                </Alert>
              )}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={uploading}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleUpload()}
          disabled={staged.length === 0 || uploading}
        >
          {uploading
            ? 'Uploading…'
            : staged.length > 1
              ? `Upload ${staged.length} files`
              : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
